<script>
  import { business, menuDraft, showPopup } from "../stores.js";
  import * as BusinessDAL from "../dal/business-dal";
  import * as BusinessBL from "../bl/business-bl";
  import MenuItemEdit from "./MenuItemEdit.svelte";
  import EditMenuItem from "./EditMenuItem.svelte";
  import { flip } from "svelte/animate";
  import { querystring, push, pop, location } from "svelte-spa-router";
  import LoadingAnim from "./LoadingAnim.svelte";
  import { onMount, tick } from "svelte";
  import FaTrashAlt from "svelte-icons/fa/FaTrashAlt.svelte";
  import clone from "just-clone";
  import TiArrowBack from "svelte-icons/ti/TiArrowBack.svelte";
  import FaExclamationTriangle from "svelte-icons/fa/FaExclamationTriangle.svelte";
  import { fade } from "svelte/transition";
  import { itemNameTaken } from "../utils.js";
  import { get } from "svelte/store";

  export let params;

  let loaded = false;

  let changed = false;
  let saving = false;
  let errors = [];

  let ghostText;

  let itemDraft;

  let mode; //edit or new

  function checkQueryString() {
    if (!$menuDraft) return;
    let sp = new URLSearchParams($querystring);
    if (sp.has("edit")) {
      mode = "edit";
      let [sectionIdx, itemIdx] = sp.get("edit").split(",");
      itemDraft = clone($menuDraft[+sectionIdx]?.items[+itemIdx]);
      itemDraft.sectionIdx = +sectionIdx;
      itemDraft.itemIdx = +itemIdx;
    } else if (sp.has("new")) {
      let sectionIdx = sp.get("new");
      mode = "new";
      itemDraft = { checkboxes: [], options: [] };
      itemDraft.sectionIdx = +sectionIdx;
    } else {
      mode = null;
      itemDraft = null;
    }
  }

  querystring.subscribe(() => {
    checkQueryString();
  });

  menuDraft.subscribe((md) => {
    if (!loaded && md != null) {
      checkQueryString();
      loaded = true;
    }
  });

  business.subscribe(async (b) => {
    if (b) {
      let menu = JSON.parse(JSON.stringify(b.menu));
      menu.forEach((section, idx) => {
        section.id = idx;
      });

      $menuDraft = menu;

      //resize section title inputs
      await tick();
      let inputs = document.querySelectorAll(".sectionTitle > input");
      inputs.forEach((i) => {
        resizeInput(i);
      });
    }
  });

  $: {
    if (!$business || $business.code != params.code) {
      BusinessBL.getBusiness(params.code);
    }
  }

  function getNewId() {
    return Date.now();
  }

  function getNewSectionName() {
    let newName = "חלק חדש";
    while (
      $menuDraft.find((section) => {
        return section.title == newName;
      })
    ) {
      newName += "(עותק)";
    }
    return newName;
  }

  function edit(sectionIdx, itemIdx) {
    push(`${$location}?edit=${sectionIdx},${itemIdx}`);
  }

  function copy(sectionIdx, itemIdx) {
    let itemCopy = clone($menuDraft[sectionIdx].items[itemIdx]);

    itemCopy.id = Date.now();
    itemCopy.sectionIdx = sectionIdx;
    itemCopy.itemIdx = itemIdx;

    //rename if name exists
    itemCopy.name += " (עותק)";
    while (itemNameTaken(itemCopy, "new", $menuDraft)) {
      itemCopy.name += " (עותק)";
    }

    $menuDraft[sectionIdx].items.splice(itemIdx + 1, 0, itemCopy);
    $menuDraft = $menuDraft;
    changed = true;
  }

  function newItem(sectionIdx) {
    push(`${$location}?new=${sectionIdx}`);
  }

  function deleteItem(sectionIdx, itemIdx) {
    showPopup("האם אתה בטוח?", () => {
      $menuDraft[sectionIdx].items.splice(itemIdx, 1);
      $menuDraft = $menuDraft;
      changed = true;
    });
  }

  function deleteSection(sectionIdx) {
    showPopup("האם אתה בטוח?", () => {
      $menuDraft.splice(sectionIdx, 1);
      $menuDraft = $menuDraft;
      changed = true;
    });
  }

  async function addSection() {
    $menuDraft.push({
      title: getNewSectionName(),
      items: [],
      id: getNewId(),
    });
    $menuDraft = $menuDraft;
    await tick();

    //find added section title and focus select
    let inputs = document.getElementsByTagName("input");
    inputs[inputs.length - 1].select();
    changed = true;
  }

  async function save(e) {
    try {
      let saveItem = e.detail;
      let sectionIdx = saveItem.sectionIdx;
      let itemIdx = saveItem.itemIdx;

      saveItem.justSaved = true;

      delete saveItem.sectionIdx;
      delete saveItem.itemIdx;

      switch (mode) {
        case "edit":
          // we need to remove the whole item element, render and readd the updated version,
          // otherwise inner elements bindings conflict new item namings
          // we also need to run component base code for some initializations
          $menuDraft[sectionIdx].items.splice(itemIdx, 1);
          $menuDraft = $menuDraft;
          await tick();
          $menuDraft[sectionIdx].items.splice(itemIdx, 0, saveItem);
          $menuDraft = $menuDraft;
          break;

        case "new":
          saveItem.id = getNewId();
          $menuDraft[sectionIdx].items = [
            ...$menuDraft[sectionIdx].items,
            saveItem,
          ];
      }
      await pop();
      changed = true;
    } catch (err) {
      showPopup("שגיאה");
    }
  }

  function resizeInput(input) {
    ghostText.innerText = input.value;
    input.style.width = ghostText.offsetWidth + 20 + "px";
  }

  function validateSectionTitle(section, idx) {
    //check empty
    errors[idx] = false;
    if (section.title.trim() === "") {
      errors[idx] = "ריק";
    }

    //check if exists
    let exists = $menuDraft.find((s) => {
      return s != section && section.title == s.title;
    });

    if (exists) {
      errors[idx] = "שם קיים";
    }
  }

  function moveSectionUp(idx) {
    if (idx == 0) return;
    let temp = $menuDraft[idx - 1];
    $menuDraft[idx - 1] = $menuDraft[idx];
    $menuDraft[idx] = temp;
    changed = true;
  }

  function moveSectionDown(idx) {
    if (idx >= $menuDraft.length - 1) return;
    let temp = $menuDraft[idx + 1];
    $menuDraft[idx + 1] = $menuDraft[idx];
    $menuDraft[idx] = temp;
    changed = true;
  }

  function moveItemUp(sectionIdx, itemIdx) {
    let sectionItems = $menuDraft[sectionIdx].items;
    if (itemIdx > 0) {
      let temp = sectionItems[itemIdx - 1];
      sectionItems[itemIdx - 1] = sectionItems[itemIdx];
      sectionItems[itemIdx] = temp;
      $menuDraft = $menuDraft;
      changed = true;
    } else {
      //we no longer move items between sections because of possible naming conflicts
      // if (sectionIdx > 0) {
      //   let temp = sectionItems.splice(itemIdx, 1)[0]; //remove element from the array and put in temp
      //   $menuDraft[sectionIdx - 1].items.push(temp);
      //   $menuDraft = $menuDraft;
      //   changed = true;
      // }
    }
  }

  function moveItemDown(sectionIdx, itemIdx) {
    let sectionItems = $menuDraft[sectionIdx].items;
    if (itemIdx < sectionItems.length - 1) {
      let temp = sectionItems[itemIdx + 1];
      sectionItems[itemIdx + 1] = sectionItems[itemIdx];
      sectionItems[itemIdx] = temp;
      $menuDraft = $menuDraft;
      changed = true;
    } else {
      //we no longer move items between sections because of possible naming conflicts
      // if (sectionIdx <= $menuDraft.length - 1) {
      //   let temp = sectionItems.splice(itemIdx, 1)[0]; //remove element from the array and put in temp
      //   $menuDraft[sectionIdx + 1].items.splice(0, 0, temp);
      //   $menuDraft = $menuDraft;
      //   changed = true;
      // }
    }
  }

  function toggleHidden(sectionIdx, itemIdx) {
    let item = $menuDraft[sectionIdx].items[itemIdx];

    if (item.hidden) {
      item.hidden = false;
    } else {
      item.hidden = true;
    }

    $menuDraft = $menuDraft;
    changed = true;
  }

  $: hasErrors = errors.find((err) => err);

</script>

<span class="ghostText" bind:this={ghostText} />

{#if $menuDraft}
  {#if itemDraft}
    <div
      class="editItemBg"
      on:mousedown={pop}
      transition:fade={{ duration: 100 }}
    >
      <EditMenuItem {mode} item={itemDraft} on:save={save} />
    </div>
  {/if}
  <div class="menu">
    {#each $menuDraft as section, sectionIdx (section.id)}
      <div class="menuSection" animate:flip={{ duration: 750 }}>
        <div class="sectionTitle">
          <div class="moveArrows">
            <i
              class="arrow up"
              on:click={(e) => {
                moveSectionUp(sectionIdx);
              }}
            />
            <i
              class="arrow down"
              on:click={() => {
                moveSectionDown(sectionIdx);
              }}
            />
          </div>
          <div
            class="titleBtn"
            on:click={() => {
              deleteSection(sectionIdx);
            }}
          >
            <FaTrashAlt />
          </div>
          <input
            type="text"
            bind:value={section.title}
            on:input={(e) => {
              changed = true;
              resizeInput(e.target);
              validateSectionTitle(section, sectionIdx);
            }}
            class:sectionTitleError={errors[sectionIdx]}
            on:focus={(e) => {
              e.target.select();
            }}
            on:keydown={(e) => {
              if (e.code == 'Enter') {
                e.target.blur();
              }
            }}
            on:blur={async (e) => {
              if (errors[sectionIdx]) {
                //if blurred while error, revert to original
                section.title = $business.menu[sectionIdx].title;
                validateSectionTitle(section, sectionIdx);
                await tick();
                resizeInput(e.target);
              }
            }}
          />
          {#if errors[sectionIdx]}
            <span class="error">
              <span> {errors[sectionIdx]}</span>
              <div class="icon">
                <FaExclamationTriangle />
              </div>
            </span>
          {/if}
          <div
            class="titleBtn addItemBtn"
            on:click={() => {
              newItem(sectionIdx);
            }}
          >
            +
          </div>
        </div>
        {#each section.items as item, itemIdx (item.id)}
          <div animate:flip={{ duration: 500 }}>
            <MenuItemEdit
              {section}
              {item}
              on:edit={() => {
                edit(sectionIdx, itemIdx);
              }}
              on:copy={() => {
                copy(sectionIdx, itemIdx);
              }}
              on:deleteItem={(detail) => {
                deleteItem(sectionIdx, itemIdx);
              }}
              on:moveUp={() => {
                moveItemUp(sectionIdx, itemIdx);
              }}
              on:moveDown={() => {
                moveItemDown(sectionIdx, itemIdx);
              }}
              on:toggleHidden={() => {
                toggleHidden(sectionIdx, itemIdx);
              }}
            />
          </div>
        {/each}
      </div>
    {/each}
    <div class="addSectionBtn" on:click={addSection}>חלק חדש</div>

    <div class="buttons">
      <button
        class="saveBtn"
        disabled={!changed || hasErrors}
        on:click={() => {
          showPopup('האם אתה בטוח?', async () => {
            saving = true;
            let code = get(business).code;
            let menuObj = get(menuDraft);
            await BusinessDAL.saveMenu(code, menuObj);
            changed = false;
            saving = false;
          });
        }}
      >
        שמירה
      </button>
      <button class="backBtn" on:click={pop}>
        חזרה
        <div class="backIcon">
          <TiArrowBack />
        </div>
      </button>
    </div>
  </div>
  {#if saving}
    <div class="saving">
      <LoadingAnim />
    </div>
  {/if}
{/if}

<style>
  .moveArrows {
    display: flex;
    flex-direction: column;
    margin-left: 20px;
    margin-right: 10px;
  }
  .arrow {
    border-radius: 3px;
    border: solid white;
    border-width: 0 6px 6px 0;
    display: inline-block;
    padding: 4px;
  }

  .down {
    transform: rotate(45deg);
  }

  .up {
    transform: rotate(-135deg);
  }
  .error {
    font-size: 16px;
    display: flex;
    direction: initial;
    align-items: center;
    margin-right: 10px;
  }

  .error .icon {
    width: 28px;
    height: 28px;
    margin-left: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    position: relative;
  }

  .error .icon::before {
    content: "";
    width: 30%;
    height: 55%;
    background: var(--red1);
    position: absolute;
    z-index: -1;
    transform: translateY(18%);
  }

  .error span {
    font-weight: 100;
  }

  .backBtn {
    display: flex;
    align-items: center;
    height: 55px;
    font-size: 20px;
    font-weight: 500;
    border: none;
    padding-left: 15px;
    padding-right: 15px;
    cursor: pointer;
  }
  .backIcon {
    width: 35px;
    height: 35px;
    margin-right: 15px;
  }
  .ghostText {
    position: absolute;
    height: 0;
    overflow: hidden;
    white-space: pre;
    font-size: 28px;
    font-family: "Rubik";
    font-weight: 600;
  }
  .saving {
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #0008;
    z-index: 999;
  }

  .addSectionBtn {
    margin-bottom: 120px;
    background: var(--blue1);
    margin: auto;
    margin-bottom: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 24px;
    border-radius: 100px;
    margin-top: 20px;
    padding: 10px;
    width: 100%;
    max-width: 200px;
    direction: ltr;
    cursor: pointer;
  }

  .titleBtn {
    min-width: 24px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 20px;
    padding: 8px;
    border: 3px solid white;
    border-radius: 54px;
    cursor: pointer;
  }

  .menu {
    direction: rtl;
  }
  .menuSection {
    margin-bottom: 20px;
    box-shadow: 0 4px 10px #00000026;
  }

  .sectionTitle {
    background: var(--blue1);
    color: white;
    font-size: 28px;
    font-family: "Rubik";
    padding: 15px;
    font-weight: 600;
    display: flex;
    align-items: center;
  }

  .sectionTitle > input {
    padding: 0;
    margin: 0;
    background: transparent;
    color: white;
    font-weight: 800;
    border: none;
    box-sizing: content-box;
    min-width: 20px;
  }

  .sectionTitle > input:focus {
    background: white;
    color: #555555;
    padding: 5px;
    border: none;
    outline: none;
  }

  .addItemBtn {
    margin-right: auto;
    margin-left: 0;
  }

  .editItemBg {
    position: fixed;
    background: #0004;
    width: 100%;
    height: 100%;
    padding: 10px;
    box-sizing: border-box;
    z-index: 2;
    display: block;
    justify-content: center;
    padding-top: var(--nav-bar-height);
    margin-bottom: 30px;
    top: 0;
    overflow: auto;
  }

  .buttons {
    position: fixed;
    bottom: 20px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    gap: 20px;
  }

  .saveBtn {
    outline: none;
    border: none;
    background: var(--green1);
    color: white;
    font-size: 20px;
    font-weight: 500;
    padding: 15px;
    width: 150px;
    height: 55px;
    cursor: pointer;
  }

  .saveBtn:disabled {
    color: #aeaeae;
    background: repeating-linear-gradient(
      45deg,
      #ababab66 0%,
      #ababab66 15%,
      #e6e6e6bd 0%,
      #e6e6e6bd 30%
    );
    cursor: default;
  }

  :global(.sectionTitleError) {
    border: 3px solid var(--red1);
  }

</style>
